import { RefObject, useEffect, useRef, useState } from 'react';
import { Outlet, useMatch, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import queryString from 'query-string';
import { IconExpand, IconLongArrowLeft } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useLazyGetRequestsChannelsByTypeQuery } from './store/services';
import {
  setRequestChannelsByTypeIsLoading,
  setCurrentQuery,
  setRequestChannelsLoadingErrorVisible,
  setRequestChannels,
} from './store/slices/general';
import MessagesList from '../components/messages-list';
import ROUTES from '../routes';
import { Channel } from './types';

import cn from './messages.module.css';

const MessagesRequests = ({
  matchPath,
  path,
}: {
  matchPath: string;
  path: string;
}) => {
  const listScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    general: {
      requestChannels,
      currentQuery,
      requestChannelsPagination,
      requestChannelsLoadingErrorVisible,
      requestChannelsErrorText,
      employee,
      selectedChannel,
    },
  } = useAppSelector((state) => state.messages);
  const [nextChannelsLoading, setNextChannelsLoading] = useState(false);

  const [getRequestChannels, { isLoading, isFetching }] =
    useLazyGetRequestsChannelsByTypeQuery();

  const {
    selectedUserId,
    sendMessage,
    scrollRef,
    search,
    connectionStatus,
    prevConnectionStatus,
  } = useOutletContext<{
    selectedUserId?: string;
    sendMessage: SendMessage;
    scrollRef: RefObject<HTMLDivElement | null>;
    search?: string;
    connectionStatus?: string;
    prevConnectionStatus?: string;
  }>();

  const dispatch = useAppDispatch();

  const match = useMatch(matchPath);

  const getNextChannels = async (skip: string) => {
    setNextChannelsLoading(true);

    const response = await getRequestChannels({ skip, query: currentQuery });

    if (response && response.data) {
      setNextChannelsLoading(false);
    } else if (response && response.error) {
      setNextChannelsLoading(false);
    }
  };
  useEffect(() => {
    dispatch(
      setRequestChannelsByTypeIsLoading(isFetching && !nextChannelsLoading),
    );
  }, [isFetching]);

  useEffect(() => {
    if (
      selectedChannel &&
      requestChannels &&
      !requestChannels?.find((item) => item.id === selectedChannel.id)
    ) {
      const nextRequestsChannels = ([] as Channel[]).concat(requestChannels);

      nextRequestsChannels.unshift(selectedChannel);

      dispatch(setRequestChannels(nextRequestsChannels));
    }
  }, [selectedChannel, requestChannels]);

  useEffect(() => {
    if (employee) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      if (
        !search ||
        (search && parsed?.name && currentQuery) ||
        (search && !parsed?.name)
      ) {
        if (path === ROUTES.MESSAGES_REQUEST_UNREAD) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('unreadMessages')) {
            query = `${
              query ? `${query}&` : ''
            }unreadMessages=true&unreadByUserId=${employee?.id}&status=OPEN`;
          }

          getRequestChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_REQUEST_IN_PROGRESS) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('status=OPEN')) {
            query = `${query ? `${query}&` : ''}status=OPEN${
              query.includes('employeeId') ? '' : '&employeeAssigned=true'
            }&unreadMessages=false&unreadByUserId=${employee?.id}`;
          }

          getRequestChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_REQUEST_COMPLETED) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          let query = filteredQuery;

          if (!query.includes('employeeAssigned')) {
            query = `${
              query ? `${query}&` : ''
            }employeeAssigned=false&unreadMessages=false&unreadByUserId=${employee?.id}`;
          }

          getRequestChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        } else if (path === ROUTES.MESSAGES_REQUEST_ALL) {
          const nextQueryArray = currentQuery.split('&');

          const filteredQuery = nextQueryArray
            ?.filter(
              (item) =>
                item &&
                !item.includes('unreadMessages') &&
                !item.includes('unreadByUserId') &&
                !item.includes('status') &&
                !item.includes('employeeAssigned'),
            )
            ?.join('&');

          const query = filteredQuery;

          getRequestChannels({
            query,
          });

          dispatch(setCurrentQuery(query));
        }
      }
    }
  }, [path, currentQuery, employee, search]);

  useEffect(() => {
    if (
      connectionStatus !== prevConnectionStatus &&
      prevConnectionStatus === 'Closed'
    ) {
      getRequestChannels({
        query: currentQuery,
      });
    }
  }, [connectionStatus, prevConnectionStatus, currentQuery]);

  useEffect(() => {
    let title = 'Messages: Requests';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return (
    <>
      <MessagesList
        messages={requestChannels}
        isLoading={
          isLoading || (isFetching && !nextChannelsLoading) || !employee
        }
        matchPath={matchPath}
        path={path}
        userId={employee?.id as string}
        pagination={requestChannelsPagination}
        nextChannelsLoading={nextChannelsLoading}
        scrollRef={listScrollRef}
        getNextChannels={getNextChannels}
        selectedUserId={selectedUserId}
        sendMessage={sendMessage}
      />
      {requestChannelsLoadingErrorVisible && (
        <div className={cn.messageLoadError}>
          <span>{requestChannelsErrorText}</span>
          <button
            type="button"
            className={cn.buttonClose}
            aria-label="Close button"
            onClick={() =>
              dispatch(setRequestChannelsLoadingErrorVisible(false))
            }
          >
            <IconExpand />
          </button>
        </div>
      )}
      {!match && !isLoading && (
        <div
          className={`${cn.messagesContentRight} ${cn.messagesContentRightEmpty}`}
        >
          <div className={cn.messagesContentRightHint}>
            <IconLongArrowLeft />
            Select a chat from the list
          </div>
        </div>
      )}
      <Outlet
        context={{
          sendMessage,
          scrollRef,
          connectionStatus,
          prevConnectionStatus,
        }}
      />
    </>
  );
};

export default MessagesRequests;
